<script setup>
// Lista de grupos de permissão da conta. Mostra grupos do sistema (read-only)
// e custom. Excluir é desabilitado em system_default. Criar exige permissão
// granular `settings.permission_groups`.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';

import SettingsPageLayout from 'dashboard/components-next/layout/SettingsPageLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { hasPermission } = usePermissions();

const records = useMapGetter('permissionGroups/getPermissionGroups');
const uiFlags = useMapGetter('permissionGroups/getUIFlags');

const showDeleteConfirm = ref(false);
const selectedGroup = ref(null);

const canManage = computed(() => hasPermission('settings.permission_groups'));

const onClickCreate = () => {
  if (!canManage.value) return;
  router.push({ name: 'permission_groups_new' });
};

const onClickEdit = group => {
  router.push({
    name: 'permission_groups_edit',
    params: { id: group.id },
  });
};

const openDeleteConfirm = group => {
  selectedGroup.value = group;
  showDeleteConfirm.value = true;
};

const closeDeleteConfirm = () => {
  selectedGroup.value = null;
  showDeleteConfirm.value = false;
};

const confirmDelete = async () => {
  const group = selectedGroup.value;
  if (!group) return;
  try {
    await store.dispatch('permissionGroups/delete', group.id);
    useAlert(t('PERMISSION_GROUPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('PERMISSION_GROUPS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDeleteConfirm();
  }
};

const formatTimestamp = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

onMounted(() => {
  store.dispatch('permissionGroups/get');
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('PERMISSION_GROUPS.HEADER')"
    :description="$t('PERMISSION_GROUPS.DESCRIPTION')"
    :is-loading="uiFlags.isFetching"
    :is-empty="!uiFlags.isFetching && !records.length"
  >
    <template #actions>
      <NextButton
        icon="i-lucide-plus"
        size="sm"
        :label="$t('PERMISSION_GROUPS.ADD')"
        :disabled="!canManage"
        @click="onClickCreate"
      />
    </template>

    <!-- Estado vazio premium -->
    <template #emptyState>
      <div
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <div
          class="size-16 rounded-full bg-n-alpha-1 flex items-center justify-center mb-4"
        >
          <i class="i-lucide-shield-check size-8 text-n-slate-10" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1">
          {{ $t('PERMISSION_GROUPS.LIST.EMPTY_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 max-w-md">
          {{ $t('PERMISSION_GROUPS.LIST.EMPTY_HINT') }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-2 pt-2 pb-10">
        <ol class="flex flex-col gap-2">
          <li
            v-for="group in records"
            :key="group.id"
            class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors px-4 py-3 flex items-center gap-4"
          >
            <span
              class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
              :class="group.systemDefault ? 'bg-n-brand/10' : 'bg-n-alpha-2'"
            >
              <i
                class="size-5"
                :class="[
                  group.systemDefault
                    ? 'i-lucide-shield-check text-n-brand'
                    : 'i-lucide-shield text-n-slate-11',
                ]"
              />
            </span>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <p class="text-sm font-semibold text-n-slate-12 truncate mb-0">
                  {{ group.name }}
                </p>
                <span
                  v-if="group.systemDefault"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-n-brand/10 text-[10px] font-medium text-n-brand uppercase tracking-wide"
                >
                  {{ $t('PERMISSION_GROUPS.SYSTEM_BADGE') }}
                </span>
              </div>
              <p
                v-if="group.description"
                class="text-xs text-n-slate-11 mt-0.5 mb-0 line-clamp-1"
              >
                {{ group.description }}
              </p>
              <div class="flex items-center gap-4 mt-1 text-xs text-n-slate-10">
                <span class="inline-flex items-center gap-1">
                  <i class="i-lucide-users size-3" />
                  {{
                    $t('PERMISSION_GROUPS.USERS_COUNT', {
                      n: group.accountUsersCount || 0,
                    })
                  }}
                </span>
                <span
                  v-if="group.createdAt"
                  class="inline-flex items-center gap-1"
                >
                  <i class="i-lucide-calendar size-3" />
                  {{ formatTimestamp(group.createdAt) }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-pen-square"
                :label="
                  group.systemDefault
                    ? $t('PERMISSION_GROUPS.LIST.VIEW')
                    : $t('PERMISSION_GROUPS.LIST.EDIT')
                "
                @click="onClickEdit(group)"
              />
              <NextButton
                v-if="!group.systemDefault"
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                :disabled="!canManage"
                :label="$t('PERMISSION_GROUPS.LIST.DELETE')"
                @click="openDeleteConfirm(group)"
              />
            </div>
          </li>
        </ol>

        <woot-delete-modal
          v-model:show="showDeleteConfirm"
          :on-close="closeDeleteConfirm"
          :on-confirm="confirmDelete"
          :title="$t('PERMISSION_GROUPS.DELETE.CONFIRM.TITLE')"
          :message="$t('PERMISSION_GROUPS.DELETE.CONFIRM.MESSAGE')"
          :message-value="selectedGroup ? ` ${selectedGroup.name}?` : ''"
          :confirm-text="$t('PERMISSION_GROUPS.DELETE.CONFIRM.YES')"
          :reject-text="$t('PERMISSION_GROUPS.DELETE.CONFIRM.NO')"
        />
      </div>
    </template>
  </SettingsPageLayout>
</template>
